import {ArchivedProposal} from '../archived-proposal';
import {BeneficiaryLink} from '../beneficiaries/beneficiary-link';
import {TrusteeLink} from '../beneficiaries/trustee-link';
import {CivilStatus, Sex} from '../dropdown-options';
import {BASE_FUNERAL_COST} from '../fna';
import {Duration} from '../insurance/duration';
import {RiderType} from '../insurance/person-insurance';
import {ProviderId} from '../insurance/providers/providers';
import {Insurer} from '../insurer';
import {
  ClientAgentAffiliationTime,
  ConfirmationStep,
  IdDocumentType,
  MinifiedProposal,
  PaymentType,
  Proposal,
} from '../proposal';
import {CurrentlyWorking, Drug} from '../questionnaire';
import {WeightUnit} from '../questionnaire/weight';
import {InsuranceType, SaleReportStatus, SaleReportType} from '../sale';
import {EMMA_DEFAULT_EMAIL} from '../url/emma';
import {Version} from '../version';
import {MOCK_ARCHIVED_USER_INFO, MOCK_USER_INFO} from './mock-user-info';
import {Province} from '../province';
import {InsuranceFirm, insuranceFirmAgentInfo} from '../insurance';

export const MOCK_MINIFIED_PROPOSAL: MinifiedProposal = {
  id: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
  userId: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
  submittedAt: new Date(),
  submitter: {
    id: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
    firstName: 'John',
    lastName: 'Doe',
    phone: '123-456-7890',
  },
  agents: [
    {
      agentId: InsuranceFirm.EMMA,
      isPrimary: false,
    },
    {
      agentId: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
      isPrimary: true,
    },
  ],
  extra: {
    idDocument: {
      type: IdDocumentType.DRIVER_LICENSE,
      code: '123456789',
    },
    sentAt: new Date(),
  },
  protection: {
    personInsurances: [
      {
        coverage: 200_000,
        duration: Duration.T10,
        price: 13.33,
      },
      {
        coverage: 10_000,
        duration: Duration.P100,
        price: 20.12,
      },
    ],
  },
};

export const MOCK_PROPOSAL: Proposal = {
  id: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
  agents: [
    {
      agentId: InsuranceFirm.EMMA,
      commission: 50,
      isServicing: true,
      isPrimary: false,
      isImmutable: true,
    },
    {
      agentId: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
      commission: 50,
      isServicing: false,
      isPrimary: true,
      isImmutable: true,
    },
  ],
  providerId: ProviderId.HUMANIA_EMMA_V2,
  providerVersion: {healthQuestionnaireVersion: Version.V1, ratesVersion: Version.V1},
  jobCategoryId: 1,
  insuree: MOCK_USER_INFO,
  payer: MOCK_USER_INFO,
  submitter: MOCK_USER_INFO,
  coverage: {
    lifeInsurances: {[Duration.T10]: 200_000, [Duration.P100]: 10_000},
    riders: [{type: RiderType.CHILD, coverage: 15000}],
  },
  beneficiaries: [
    {
      firstName: 'Jane',
      lastName: 'Doe',
      birthday: new Date(new Date().getFullYear() - 20, new Date().getMonth()),
      link: BeneficiaryLink.BROTHER,
      revocable: true,
      share: 100,
      trustee: {
        firstName: 'John',
        lastName: 'Doe',
        birthday: new Date(new Date().getFullYear() - 20, new Date().getMonth()),
        sex: Sex.FEMALE,
        link: TrusteeLink.MOTHER,
      },
    },
  ],
  protection: {
    personInsurances: [
      {
        priceBreakdown: {
          basePremium: 10,
          policyFee: 0,
          surchargeAmount: 2.5,
          baseAnnualPremium: 20,
          policyAnnualFee: 0,
          surchargeAnnualAmount: 2.5,
          isBaseInsurance: false,
        },
        type: InsuranceType.LIFE,
        coverage: 650_000,
        duration: Duration.T35,
        price: 10.55,
        annualPrice: 117.23,
      },
      {
        priceBreakdown: {
          basePremium: 10,
          policyFee: 5.4,
          surchargeAmount: 2.5,
          baseAnnualPremium: 20,
          policyAnnualFee: 60,
          surchargeAnnualAmount: 2.5,
          isBaseInsurance: true,
        },
        type: InsuranceType.LIFE,
        coverage: 20_000,
        duration: Duration.P100,
        price: 22.25,
        annualPrice: 247.23,
        riders: [{type: RiderType.CHILD, coverage: 25_000, price: 11.25, annualPrice: 125}],
      },
    ],
  },
  healthAnswers: {
    citizenship: true,
    birthday: new Date(2000, 1, 1),
    sex: Sex.FEMALE,
    height: 200,
    weight: {
      value: 200,
      unit: WeightUnit.POUNDS,
    },
    smoker: false,
    marijuana: false,
    currentlyWorking: CurrentlyWorking.YES,
    drugUse: Drug.NO,
    drugTreatment: Drug.NO,
    criminalOffence: false,
    diabetesDiagnosis: false,
    discomfortSymptoms: false,
    extremeActivities: false,
    respiratoryDisorders: false,
    muscularDisorders: false,
    neuralDisorders: false,
  },
  paymentInfo: {
    type: PaymentType.CREDIT,
  },
  extra: {
    idDocument: {
      type: IdDocumentType.DRIVER_LICENSE,
      code: 'B3eT',
    },
    clientAgentAffiliationTime: ClientAgentAffiliationTime.BETWEEN_THREE_AND_FIVE_YEARS,
  },
  hasPolicyReplacementNotice: false,
  versionHash: '1984fdce-d27e-11ee-b365-ef5cf3da82eb',
};

export const MOCK_ARCHIVED_PROPOSAL: ArchivedProposal = {
  id: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
  agents: [
    {
      agentId: InsuranceFirm.EMMA,
      commission: 50,
      isServicing: true,
      isPrimary: false,
      firstName: insuranceFirmAgentInfo.EMMA.firstName,
      lastName: insuranceFirmAgentInfo.EMMA.lastName,
      email: EMMA_DEFAULT_EMAIL,
    },
    {
      agentId: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
      commission: 50,
      isServicing: false,
      isPrimary: true,
      firstName: 'Beetle',
      lastName: 'Juice',
      email: 'beetle@juice.ca',
    },
  ],
  providerId: ProviderId.HUMANIA_EMMA_V2,
  providerVersion: {healthQuestionnaireVersion: Version.V1, ratesVersion: Version.V1},
  insuree: MOCK_ARCHIVED_USER_INFO,
  payer: MOCK_ARCHIVED_USER_INFO,
  submitter: MOCK_ARCHIVED_USER_INFO,
  beneficiaries: [
    {
      firstName: 'Jane',
      lastName: 'Doe',
      birthday: new Date(),
      link: BeneficiaryLink.BROTHER,
      revocable: true,
      share: 100,
      trustee: {
        firstName: 'John',
        lastName: 'Doe',
        birthday: new Date(new Date().getFullYear() - 20, new Date().getMonth()),
        sex: Sex.FEMALE,
        link: TrusteeLink.MOTHER,
      },
    },
  ],
  protection: {
    personInsurances: [
      {
        priceBreakdown: {
          basePremium: 28.08,
          policyFee: 0,
          surchargeAmount: 0,
          baseAnnualPremium: 312,
          policyAnnualFee: 0,
          surchargeAnnualAmount: 0,
          isBaseInsurance: false,
        },
        type: InsuranceType.LIFE,
        coverage: 650_000,
        duration: Duration.T35,
        price: 28.08,
        annualPrice: 312,
      },
      {
        priceBreakdown: {
          basePremium: 9.5,
          policyFee: 5.4,
          surchargeAmount: 0,
          baseAnnualPremium: 105.6,
          policyAnnualFee: 60,
          surchargeAnnualAmount: 0,
          isBaseInsurance: true,
        },
        type: InsuranceType.LIFE,
        coverage: 20_000,
        duration: Duration.P100,
        price: 26.15,
        annualPrice: 290.6,
        riders: [{type: RiderType.CHILD, coverage: 25_000, price: 11.25, annualPrice: 125}],
      },
    ],
  },
  healthAnswers: {
    citizenship: true,
    birthday: new Date(2000, 1, 1),
    sex: Sex.FEMALE,
    height: 200,
    weight: {
      value: 200,
      unit: WeightUnit.POUNDS,
    },
    smoker: false,
    marijuana: false,
    currentlyWorking: CurrentlyWorking.YES,
    drugUse: Drug.NO,
    drugTreatment: Drug.NO,
    criminalOffence: false,
    diabetesDiagnosis: false,
    discomfortSymptoms: false,
    extremeActivities: false,
    respiratoryDisorders: false,
    muscularDisorders: false,
    neuralDisorders: false,
  },
  paymentInfo: {
    type: PaymentType.CREDIT,
  },
  extra: {
    idDocument: {
      type: IdDocumentType.DRIVER_LICENSE,
      code: '123456789',
    },
    clientAgentAffiliationTime: ClientAgentAffiliationTime.BETWEEN_THREE_AND_FIVE_YEARS,
  },
  hasPolicyReplacementNotice: false,
  saleReport: {
    id: '55034768-aa4e-11ee-ace1-0f85f03b80a0',
    policyNumber: 'EM123V',
    insurer: Insurer.CANADA_LIFE,
    protection: {
      personInsurances: [
        {
          priceBreakdown: {
            basePremium: 10,
            policyFee: 0,
            surchargeAmount: 2.5,
            baseAnnualPremium: 20,
            policyAnnualFee: 0,
            surchargeAnnualAmount: 2.5,
            isBaseInsurance: false,
          },
          type: InsuranceType.LIFE,
          coverage: 650_000,
          duration: Duration.T35,
          price: 10.55,
          annualPrice: 117.23,
        },
      ],
      surchargeRate: 0,
    },
    fna: {
      id: '44034768-aa4e-11ee-ace1-0f85f03b80a7',
      job: 'Vendeur de dope',
      province: Province.QC,
      civilStatus: CivilStatus.SINGLE,
      numChildren: 2,
      youngestChildAgeInMonths: 12,
      salary: 75_000,
      mortgageAmount: 300_000,
      debtAmount: 100_000,
      savingsAmount: 50_000,
      heritageAmount: 10_000,
      currentProtections: [
        {
          coverageAmount: 100_000,
          issueDate: new Date('2021-01-01'),
          issuerName: 'Sun Life',
          coverageType: InsuranceType.LIFE,
        },
      ],
      agents: [
        {
          agentId: '5984fdce-d27e-11ee-a365-ef5cf3da82eb',
          firstName: 'Beetle',
          lastName: 'Juice',
          email: 'beetle@juice.ca',
        },
      ],
      salaryReplacement: 100_000,
      funeralCost: BASE_FUNERAL_COST,
      recommendationTemporaryNeeds: 290_000,
      recommendationPermanentNeeds: 10_000,
      needs: 300_000,
      recommendationNeeds: 300_000,
    },
    proposal: {} as ArchivedProposal,
    type: SaleReportType.INTERNAL,
    status: SaleReportStatus.UNDERWRITING,
    createdAt: new Date('2024-01-01'),
    contractReceived: false,
  },
  confirmationSteps: {
    [ConfirmationStep.PersonalInfo]: new Date('2000-01-01'),
    [ConfirmationStep.Address]: new Date('2000-01-01'),
    [ConfirmationStep.Protection]: new Date('2000-01-01'),
    [ConfirmationStep.AnswerValidation]: new Date('2000-01-01'),
    [ConfirmationStep.Disclosures]: new Date('2000-01-01'),
    [ConfirmationStep.ESignature]: new Date('2000-01-01'),
    [ConfirmationStep.Payment]: new Date('2000-01-01'),
  },
  submittedAt: new Date('2024-08-21'),
};
